import { AGENT_EXTRA_TABS } from "@/components/agents/agent-extra-tabs";
import { cn } from "@/lib/utils";
import { Link, useRouterState } from "@tanstack/react-router";

/**
 * An agent's sub-nav, the same shape as `ProjectTabs`. The counts are
 * on the tabs because the whole point of splitting the page up is that
 * you can tell from the tab strip whether a tab is worth opening.
 */

const TAB_CLASS =
  "inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-4 py-3 font-medium text-sm transition-colors";
const ACTIVE = "border-primary text-primary";
const INACTIVE =
  "border-transparent text-muted-foreground hover:border-border hover:text-foreground";

export function AgentTabs({
  slug,
  hasVerifications,
  changeCount,
  runCount,
}: {
  slug: string;
  hasVerifications: boolean;
  changeCount: number;
  runCount: number;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const base = `/agents/${slug}`;

  const tabs: Array<{ label: string; segment: string; count?: number }> = [
    { label: "Overview", segment: "" },
    { label: "Activity", segment: "activity", count: runCount },
    ...(hasVerifications ? [{ label: "Verifications", segment: "verifications" }] : []),
    { label: "Changes", segment: "changes", count: changeCount },
    ...(AGENT_EXTRA_TABS[slug] ?? []).map((t) => ({ label: t.label, segment: t.segment })),
    { label: "Manual", segment: "manual" },
  ];

  return (
    <div className="mb-4 flex gap-0 overflow-x-auto border-border border-b" role="tablist">
      {tabs.map((tab) => {
        const href = tab.segment ? `${base}/${tab.segment}` : base;
        const active = tab.segment
          ? pathname.startsWith(href)
          : pathname === base || pathname === `${base}/`;
        return (
          <Link
            key={tab.segment || "overview"}
            to={href}
            role="tab"
            aria-selected={active}
            className={cn(TAB_CLASS, active ? ACTIVE : INACTIVE)}
          >
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className="rounded-full bg-muted px-1.5 text-xs">{tab.count}</span>
            )}
          </Link>
        );
      })}
    </div>
  );
}
